import { useCallback, useEffect, useState, type FormEvent } from "react"

import { useAuth, useSignIn, useSignUp } from "@clerk/react"
import { Navigate } from "@tanstack/react-router"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthShell } from "@/features/auth/auth-shell"

const FLEET_INVITE_FALLBACK_REDIRECT_URL = "/fleet/onboarding/organization"

interface FleetInviteAcceptancePageProps {
  redirectUrl?: string
  status?: string
  ticket?: string
}

type InviteAccountStatus = "sign_in" | "sign_up" | "complete"
type FleetInviteSignUpSnapshot = {
  status?: unknown
  missingFields?: unknown
  requiredFields?: unknown
  unverifiedFields?: unknown
  emailAddress?: unknown
}

function normalizeInviteAccountStatus(value: string | undefined): InviteAccountStatus {
  if (value === "sign_in" || value === "complete") {
    return value
  }

  return "sign_up"
}

function normalizeClerkFieldName(field: unknown): string | null {
  if (typeof field !== "string") {
    return null
  }

  const trimmedField = field.trim()
  if (trimmedField.length === 0) {
    return null
  }

  const withUnderscores = trimmedField
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase()

  return withUnderscores
}

function collectNormalizedClerkFields(value: unknown): Set<string> {
  if (!Array.isArray(value)) {
    return new Set()
  }

  const fields = new Set<string>()
  for (const entry of value) {
    const normalizedField = normalizeClerkFieldName(entry)
    if (normalizedField) {
      fields.add(normalizedField)
    }
  }

  return fields
}

function normalizeEmailAddress(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : null
}

export function resolveFleetInviteCredentialRequirements(signUp: FleetInviteSignUpSnapshot | null | undefined) {
  const status = typeof signUp?.status === "string" ? signUp.status : null
  const missingFields = collectNormalizedClerkFields(signUp?.missingFields)
  const requiredFields = collectNormalizedClerkFields(signUp?.requiredFields)
  const unverifiedFields = collectNormalizedClerkFields(signUp?.unverifiedFields)
  const needsEmailAddress = missingFields.has("email_address")
  const needsPassword = missingFields.has("password")
  const needsCredentialStep = status === "missing_requirements" && (needsEmailAddress || needsPassword)
  const needsEmailVerificationStep =
    status === "missing_requirements" &&
    !needsCredentialStep &&
    unverifiedFields.has("email_address")

  return {
    inviteEmailAddress: normalizeEmailAddress(signUp?.emailAddress),
    isMissingRequirements: status === "missing_requirements",
    needsCredentialStep,
    needsEmailVerificationStep,
    needsEmailAddress,
    needsPassword,
    missingFields: Array.from(missingFields),
    unverifiedFields: Array.from(unverifiedFields),
    requiredFields: Array.from(requiredFields),
  }
}

function formatRemainingSignUpRequirements(summary: {
  missingFields: string[]
  unverifiedFields: string[]
}) {
  const details: string[] = []

  if (summary.missingFields.length > 0) {
    details.push(`missing: ${summary.missingFields.join(", ")}`)
  }

  if (summary.unverifiedFields.length > 0) {
    details.push(`verify: ${summary.unverifiedFields.join(", ")}`)
  }

  if (details.length === 0) {
    return null
  }

  return details.join(" | ")
}

function collectClerkErrorMessages(error: unknown, messages: string[]) {
  if (!error) {
    return
  }

  if (typeof error === "string") {
    const message = error.trim()
    if (message.length > 0) {
      messages.push(message)
    }
    return
  }

  if (Array.isArray(error)) {
    for (const entry of error) {
      collectClerkErrorMessages(entry, messages)
    }
    return
  }

  if (typeof error !== "object") {
    return
  }

  const entry = error as Record<string, unknown>
  const messageKeys = [
    "longMessage",
    "long_message",
    "message",
    "shortMessage",
    "short_message",
  ]

  for (const key of messageKeys) {
    const message = entry[key]
    if (typeof message === "string") {
      const trimmedMessage = message.trim()
      if (trimmedMessage.length > 0) {
        messages.push(trimmedMessage)
      }
    }
  }

  if ("fields" in entry) {
    collectClerkErrorMessages(entry.fields, messages)
  }

  if ("errors" in entry) {
    collectClerkErrorMessages(entry.errors, messages)
  }

  for (const value of Object.values(entry)) {
    if (value && typeof value === "object") {
      collectClerkErrorMessages(value, messages)
    }
  }
}

export function formatClerkError(error: unknown) {
  if (!error) {
    return null
  }

  const messages: string[] = []
  collectClerkErrorMessages(error, messages)
  const uniqueMessages = Array.from(new Set(messages)).filter((message) => {
    const normalizedMessage = message.toLowerCase()
    return !normalizedMessage.includes("captcha") && !normalizedMessage.includes("verification challenge")
  })

  if (uniqueMessages.length === 0) {
    return null
  }

  return uniqueMessages.join(" ")
}

export function FleetInviteAcceptancePage({
  redirectUrl,
  status,
  ticket,
}: FleetInviteAcceptancePageProps) {
  const { isLoaded, isSignedIn } = useAuth()
  const { errors: signInErrors, signIn } = useSignIn()
  const { errors: signUpErrors, fetchStatus, signUp } = useSignUp()
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [emailAddress, setEmailAddress] = useState("")
  const [password, setPassword] = useState("")
  const [emailVerificationCode, setEmailVerificationCode] = useState("")
  const [emailCodeSent, setEmailCodeSent] = useState(false)
  const [emailCodeSendInFlight, setEmailCodeSendInFlight] = useState(false)
  const [autoEmailCodeSendAttempted, setAutoEmailCodeSendAttempted] = useState(false)
  const [flowError, setFlowError] = useState<string | null>(null)
  const [signInAttemptedTicket, setSignInAttemptedTicket] = useState<string | null>(null)

  const inviteStatus = normalizeInviteAccountStatus(status)
  const invitationTicket = ticket?.trim() || null
  const fallbackRedirectUrl = redirectUrl?.trim() || FLEET_INVITE_FALLBACK_REDIRECT_URL
  const signInErrorMessage = flowError ?? formatClerkError(signInErrors)
  const signUpErrorMessage = flowError ?? formatClerkError(signUpErrors)
  const signUpRequirements = resolveFleetInviteCredentialRequirements(signUp)
  const showCredentialStep = signUpRequirements.needsCredentialStep
  const showEmailVerificationStep = signUpRequirements.needsEmailVerificationStep
  const needsEmailAddress = showCredentialStep && signUpRequirements.needsEmailAddress
  const needsPassword = showCredentialStep && signUpRequirements.needsPassword

  useEffect(() => {
    if ((!showCredentialStep && !showEmailVerificationStep) || !signUpRequirements.inviteEmailAddress) {
      return
    }

    setEmailAddress((currentValue) => {
      if (currentValue.trim().length > 0) {
        return currentValue
      }

      return signUpRequirements.inviteEmailAddress ?? currentValue
    })
  }, [showCredentialStep, showEmailVerificationStep, signUpRequirements.inviteEmailAddress])

  const sendFleetInviteEmailCode = useCallback(async () => {
    setEmailCodeSendInFlight(true)
    const { error } = await signUp.verifications.sendEmailCode()
    setEmailCodeSendInFlight(false)

    if (error) {
      setFlowError(formatClerkError(error) ?? "Could not send the email verification code. Please try again.")
      return false
    }

    setEmailCodeSent(true)
    return true
  }, [signUp])

  useEffect(() => {
    if (!showEmailVerificationStep || emailCodeSent || emailCodeSendInFlight || autoEmailCodeSendAttempted) {
      return
    }

    setAutoEmailCodeSendAttempted(true)
    let cancelled = false
    const sendCode = async () => {
      const didSend = await sendFleetInviteEmailCode()
      if (cancelled || !didSend) {
        return
      }
    }

    void sendCode()

    return () => {
      cancelled = true
    }
  }, [
    showEmailVerificationStep,
    emailCodeSent,
    emailCodeSendInFlight,
    autoEmailCodeSendAttempted,
    sendFleetInviteEmailCode,
  ])

  useEffect(() => {
    if (showEmailVerificationStep) {
      return
    }

    setEmailVerificationCode("")
    setEmailCodeSent(false)
    setEmailCodeSendInFlight(false)
    setAutoEmailCodeSendAttempted(false)
  }, [showEmailVerificationStep])

  const finalizeFleetInviteFlow = async (
    mode: "sign_in" | "sign_up"
  ) => {
    const operation = mode === "sign_in" ? signIn.finalize : signUp.finalize

    const { error } = await operation({
      navigate: ({ decorateUrl, session }) => {
        if (session?.currentTask) {
          setFlowError("Finish the required account task, then continue this invitation flow.")
          return
        }

        const nextUrl = decorateUrl(fallbackRedirectUrl)

        if (nextUrl.startsWith("http")) {
          window.location.href = nextUrl
          return
        }

        window.location.href = nextUrl
      },
    })

    if (error) {
      setFlowError(formatClerkError(error) ?? "Could not complete invitation acceptance. Please try again.")
    }
  }

  useEffect(() => {
    if (!isLoaded || isSignedIn || inviteStatus !== "sign_in" || !invitationTicket) {
      return
    }

    if (signInAttemptedTicket === invitationTicket) {
      return
    }

    let cancelled = false
    setSignInAttemptedTicket(invitationTicket)
    setFlowError(null)

    const completeSignIn = async () => {
      const { error } = await signIn.ticket({
        ticket: invitationTicket,
      })

      if (cancelled) {
        return
      }

      if (error) {
        setFlowError(
          formatClerkError(error) ?? "Could not complete invitation acceptance. Please try again."
        )
        return
      }

      await finalizeFleetInviteFlow("sign_in")
    }

    void completeSignIn()

    return () => {
      cancelled = true
    }
  }, [
    inviteStatus,
    invitationTicket,
    isLoaded,
    isSignedIn,
    signIn,
    signInAttemptedTicket,
  ])

  const handleSignUpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!invitationTicket) {
      setFlowError("Invitation ticket is missing from this link.")
      return
    }

    setFlowError(null)
    const firstNameValue = firstName.trim()
    const lastNameValue = lastName.trim()
    const emailAddressValue = emailAddress.trim()
    const passwordValue = password.trim()
    const emailVerificationCodeValue = emailVerificationCode.trim()

    if (showEmailVerificationStep) {
      if (emailVerificationCodeValue.length === 0) {
        setFlowError("Enter the email verification code to continue.")
        return
      }

      const { error } = await signUp.verifications.verifyEmailCode({
        code: emailVerificationCodeValue,
      })

      if (error) {
        setFlowError(
          formatClerkError(error) ?? "Could not verify your email address. Please try again."
        )
        return
      }

      if (signUp.status === "complete") {
        await finalizeFleetInviteFlow("sign_up")
        return
      }

      const remainingRequirements = resolveFleetInviteCredentialRequirements(signUp)
      const unresolvedSummary = formatRemainingSignUpRequirements(remainingRequirements)

      if (remainingRequirements.isMissingRequirements) {
        setFlowError(
          unresolvedSummary
            ? `Additional sign-up steps are still required: ${unresolvedSummary}.`
            : "Additional sign-up steps are still required to complete this invitation."
        )
        return
      }

      setFlowError("Could not complete invitation acceptance. Please try again.")
      return
    }

    if (showCredentialStep) {
      if (needsEmailAddress && emailAddressValue.length === 0) {
        setFlowError("Enter the invited email address to continue.")
        return
      }

      if (needsPassword && passwordValue.length === 0) {
        setFlowError("Create a password to finish accepting this invitation.")
        return
      }

      const { error } = await signUp.password({
        password: passwordValue,
        ...(firstNameValue ? { firstName: firstNameValue } : {}),
        ...(lastNameValue ? { lastName: lastNameValue } : {}),
        ...(emailAddressValue ? { emailAddress: emailAddressValue } : {}),
      })

      if (error) {
        setFlowError(
          formatClerkError(error) ??
            "Could not complete invitation acceptance. Please try again."
        )
        return
      }

      if (signUp.status === "complete") {
        await finalizeFleetInviteFlow("sign_up")
        return
      }

      const remainingRequirements = resolveFleetInviteCredentialRequirements(signUp)
      if (remainingRequirements.needsEmailVerificationStep) {
        return
      }

      const unresolvedSummary = formatRemainingSignUpRequirements(remainingRequirements)

      if (remainingRequirements.isMissingRequirements) {
        setFlowError(
          unresolvedSummary
            ? `Additional sign-up steps are still required: ${unresolvedSummary}.`
            : "Additional sign-up steps are still required to complete this invitation."
        )
        return
      }

      setFlowError("Could not complete invitation acceptance. Please try again.")
      return
    }

    const { error } = await signUp.ticket({
      ticket: invitationTicket,
      ...(firstNameValue ? { firstName: firstNameValue } : {}),
      ...(lastNameValue ? { lastName: lastNameValue } : {}),
    })

    if (error) {
      setFlowError(formatClerkError(error) ?? "Could not complete invitation acceptance. Please try again.")
      return
    }

    const ticketRequirements = resolveFleetInviteCredentialRequirements(signUp)
    if (ticketRequirements.needsCredentialStep || ticketRequirements.needsEmailVerificationStep) {
      if (ticketRequirements.inviteEmailAddress) {
        setEmailAddress(ticketRequirements.inviteEmailAddress)
      }
      return
    }

    if (ticketRequirements.isMissingRequirements) {
      const unresolvedSummary = formatRemainingSignUpRequirements(ticketRequirements)
      setFlowError(
        unresolvedSummary
          ? `Additional sign-up steps are still required: ${unresolvedSummary}.`
          : "Additional sign-up steps are still required to complete this invitation."
      )
      return
    }

    await finalizeFleetInviteFlow("sign_up")
  }

  if (isLoaded && isSignedIn) {
    return <Navigate to={fallbackRedirectUrl} replace />
  }

  if (!invitationTicket) {
    return (
      <AuthShell
        title="Fleet invitation link is incomplete"
        description="This invite link is missing the Clerk invitation ticket. Request a fresh Fleet Safety invite."
      >
        <div className="w-full rounded-3xl border border-border/70 bg-background/80 p-6">
          <Button asChild className="w-full">
            <a href="/sign-in?fleet_invite=1">Back to Fleet Safety sign in</a>
          </Button>
        </div>
      </AuthShell>
    )
  }

  if (inviteStatus === "complete") {
    return <Navigate to={fallbackRedirectUrl} replace />
  }

  if (inviteStatus === "sign_in") {
    return (
      <AuthShell
        title="Welcome to Fleet Safety"
        description="Verifying your invitation and signing you in to continue onboarding."
      >
        <div className="w-full space-y-4 rounded-3xl border border-border/70 bg-background/80 p-6">
          <p className="text-sm leading-6 text-muted-foreground">
            Please wait while we complete your Fleet invitation sign-in.
          </p>
          {signInErrorMessage ? (
            <p className="text-sm leading-6 text-destructive">{signInErrorMessage}</p>
          ) : null}
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Welcome to Fleet Safety"
      description="Create your Fleet Safety credentials to complete company onboarding and operator setup."
    >
      <form
        className="w-full space-y-4 rounded-3xl border border-border/70 bg-background/80 p-6"
        onSubmit={(event) => {
          void handleSignUpSubmit(event)
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground" htmlFor="fleet-invite-first-name">
              First name
            </label>
            <Input
              autoComplete="given-name"
              id="fleet-invite-first-name"
              onChange={(event) => setFirstName(event.target.value)}
              placeholder="Optional"
              value={firstName}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground" htmlFor="fleet-invite-last-name">
              Last name
            </label>
            <Input
              autoComplete="family-name"
              id="fleet-invite-last-name"
              onChange={(event) => setLastName(event.target.value)}
              placeholder="Optional"
              value={lastName}
            />
          </div>
        </div>

        {showCredentialStep ? (
          <div className="space-y-4">
            {needsEmailAddress ? (
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground" htmlFor="fleet-invite-email-address">
                  Email address
                </label>
                <Input
                  autoComplete="email"
                  id="fleet-invite-email-address"
                  onChange={(event) => setEmailAddress(event.target.value)}
                  placeholder="name@company.com"
                  type="email"
                  value={emailAddress}
                />
              </div>
            ) : null}
            {needsPassword ? (
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground" htmlFor="fleet-invite-password">
                  Password
                </label>
                <Input
                  autoComplete="new-password"
                  id="fleet-invite-password"
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Create a password"
                  type="password"
                  value={password}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {showEmailVerificationStep ? (
          <div className="space-y-4">
            <p className="text-sm leading-6 text-muted-foreground">
              We sent a verification code to{" "}
              <span className="font-medium text-foreground">{emailAddress || "your email address"}</span>.
              Enter that code to finish accepting this Fleet invitation.
            </p>
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="fleet-invite-email-code">
                Email verification code
              </label>
              <Input
                autoComplete="one-time-code"
                id="fleet-invite-email-code"
                inputMode="numeric"
                onChange={(event) => setEmailVerificationCode(event.target.value)}
                placeholder="Enter the code from your email"
                value={emailVerificationCode}
              />
            </div>
            <Button
              className="w-full"
              disabled={fetchStatus === "fetching" || emailCodeSendInFlight}
              onClick={() => {
                setFlowError(null)
                void sendFleetInviteEmailCode()
              }}
              type="button"
              variant="outline"
            >
              Resend verification code
            </Button>
            {emailCodeSent ? (
              <p className="text-sm leading-6 text-muted-foreground">
                A fresh verification code has been sent.
              </p>
            ) : null}
          </div>
        ) : null}

        {signUpErrorMessage ? (
          <p className="text-sm leading-6 text-destructive">{signUpErrorMessage}</p>
        ) : null}

        <Button className="w-full" disabled={fetchStatus === "fetching"} type="submit">
          {showEmailVerificationStep ? "Verify email and continue" : "Accept invitation and continue"}
        </Button>
      </form>
    </AuthShell>
  )
}
